import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import toast from 'react-hot-toast';

export default function AdminBlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    title: '',
    category: 'Spiritual Care',
    author: 'Chandana Aura Editor',
    image: '/assets/hero_perfect_ganesha.jpg',
    excerpt: '',
    content: '',
    tagsStr: 'Sandalwood, Mysuru, Meditation',
    isPublished: true,
  });

  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  useEffect(() => {
    if (isEdit) {
      api.get(`/blogs/admin/${id}`)
        .then((res) => {
          const b = res.data;
          setForm({
            title: b.title || '',
            category: b.category || 'Spiritual Care',
            author: b.author || 'Chandana Aura Editor',
            image: b.image || '/assets/hero_perfect_ganesha.jpg',
            excerpt: b.excerpt || '',
            content: b.content || '',
            tagsStr: (b.tags || []).join(', '),
            isPublished: b.isPublished !== undefined ? b.isPublished : true,
          });
        })
        .catch(() => toast.error('Failed to load blog details'))
        .finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const { data } = await api.post('/uploads/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const uploadedPath = data.url || data.path;
      setForm((prev) => ({ ...prev, image: uploadedPath }));
      toast.success('Cover image uploaded successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload image file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.excerpt || !form.content) {
      toast.error('Title, Excerpt, and Content are required!');
      return;
    }

    const payload = {
      ...form,
      tags: form.tagsStr.split(',').map((t) => t.trim()).filter(Boolean),
    };

    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/blogs/${id}`, payload);
        toast.success('Blog post updated successfully!');
      } else {
        await api.post('/blogs', payload);
        toast.success('Blog post created successfully!');
      }
      navigate('/admin/blogs');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save blog post');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="p-12 text-center text-xs text-[#8A796C]">Loading blog details...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Action Bar Header */}
      <div className="flex items-center justify-between bg-white p-5 rounded-2xl border border-[#E8DFD8] shadow-sm">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#2B1810]">
            {isEdit ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
          <p className="text-xs text-[#8A796C]">Publish articles for storefront blog readers</p>
        </div>

        <Link
          to="/admin/blogs"
          className="px-4 py-2 border border-[#E8DFD8] hover:bg-[#FAF8F5] text-[#2B1810] font-bold text-xs rounded-lg transition-colors"
        >
          ← Back to Blogs
        </Link>
      </div>

      {/* Main Blog Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-2xl border border-[#E8DFD8] shadow-sm space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1">Blog Post Title *</label>
            <input
              type="text"
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. The Sacred Science of Mysuru Sandalwood"
              className="input-field py-2"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="input-field py-2"
              >
                <option value="Spiritual Care">Spiritual Care</option>
                <option value="Authenticity & Guides">Authenticity & Guides</option>
                <option value="Home & Vastu">Home & Vastu</option>
                <option value="Heritage & Artisans">Heritage & Artisans</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1">Author Name</label>
              <input
                type="text"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                placeholder="Author Name"
                className="input-field py-2"
              />
            </div>
          </div>

          {/* Direct Image File Uploader & Live Preview */}
          <div className="bg-[#FAF8F5] p-4 rounded-xl border border-[#E8DFD8] space-y-3">
            <label className="block text-xs font-bold text-[#2B1810] uppercase">Blog Cover Image *</label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {form.image && (
                <div className="w-32 h-20 rounded-lg overflow-hidden border border-[#E8DFD8] shadow-sm bg-white shrink-0 relative group">
                  <img
                    src={form.image}
                    alt="Cover preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/assets/hero_perfect_ganesha.jpg';
                    }}
                  />
                </div>
              )}

              <div className="space-y-2">
                <label className="inline-flex items-center gap-2 bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-4 py-2 rounded-lg cursor-pointer shadow transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <span>{uploading ? 'Uploading File...' : 'Upload Image File Direct'}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                <p className="text-[11px] text-[#8A796C]">
                  Recommended: 1600 × 900 px (16:9). JPG, PNG or WEBP; maximum 5 MB.
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1">Short Excerpt *</label>
            <textarea
              rows="2"
              required
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              placeholder="Brief summary to show on blog cards..."
              className="input-field py-2"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1">Full Content (HTML / Text) *</label>
            <textarea
              rows="10"
              required
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              placeholder="Full blog post content. You can write standard text or HTML headers <h2> <h3> <p>..."
              className="input-field py-2 font-mono text-xs"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-[#2B1810] uppercase mb-1">Tags (Comma Separated)</label>
              <input
                type="text"
                value={form.tagsStr}
                onChange={(e) => setForm({ ...form, tagsStr: e.target.value })}
                placeholder="Sandalwood, Meditation, Vedic"
                className="input-field py-2"
              />
            </div>

            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2 cursor-pointer font-bold text-xs text-[#2B1810]">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                  className="w-4 h-4 accent-[#C49A45] rounded"
                />
                <span>Publish Immediately on Storefront Blog Page</span>
              </label>
            </div>
          </div>
        </div>

        <div className="border-t border-[#E8DFD8] pt-4 flex justify-end gap-3">
          <Link
            to="/admin/blogs"
            className="px-5 py-2.5 border border-[#E8DFD8] hover:bg-[#FAF8F5] text-[#2B1810] font-bold text-xs rounded-lg transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading || uploading}
            className="bg-[#C49A45] hover:bg-[#B08736] text-white font-bold text-xs uppercase tracking-wider px-6 py-2.5 rounded-lg shadow transition-colors"
          >
            {loading ? 'Saving...' : isEdit ? 'Update Blog Post' : 'Create Blog Post'}
          </button>
        </div>
      </form>
    </div>
  );
}
